import { useState, useEffect, useCallback } from 'react';
import {
    IconPlus, IconEdit, IconTrash, IconCategory,
    IconGripVertical, IconChevronUp, IconChevronDown,
    IconPin, IconPinFilled,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from 'react-i18next';
import {
    DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import printTemplateCategoryService from '../../../services/printTemplateCategoryService';

/**
 * Print-template categories admin — the AI-template categories screen, over the
 * print category table. Same table, same drag ordering, same single-pin rule.
 *
 * Renaming a category here also rewrites the denormalised name each print
 * template carries (see the backend route), so the legacy string column that
 * older callers still read never drifts from this row.
 */

// Dark-mode-aware status chips.
const STATUS_TONE = {
    published: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    draft:     'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const EMPTY_FORM = { name: '', nameBn: '', slug: '', description: '', descriptionBn: '', status: 'draft', sortOrder: 0 };

function slugify(text) {
    return String(text).toLowerCase().trim()
        .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

// Icon button with a proper (Radix) tooltip — the actions column uses these
// instead of native `title` so the label appears instantly on hover.
function ActionBtn({ label, onClick, disabled = false, className, children }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className={className}>
                    {children}
                </button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    );
}

/* ─── Sortable row ───────────────────────────────────────────────────────────── */
function SortableRow({ cat, index, total, fmtNum, onEdit, onDelete, onToggleStatus, onTogglePin, onMoveUp, onMoveDown }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 1 : undefined,
    };

    const isPublished = cat.status === 'published';
    const actionBtnCls = 'w-7 h-7 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center transition-colors';

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 ${isDragging ? 'bg-primary/5' : ''}`}
        >
            <td className="px-2 py-2.5 w-9">
                <span
                    {...attributes}
                    {...listeners}
                    className="cursor-grab flex items-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 touch-none"
                >
                    <IconGripVertical size={16} />
                </span>
            </td>
            <td className="px-3 py-2.5">
                <p className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5">
                    {cat.name}
                    {cat.pinned && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary" title={t('printTemplateCategoriesAdmin.pinnedBadge')}>
                            <IconPinFilled size={10} /> {t('printTemplateCategoriesAdmin.pinnedBadge')}
                        </span>
                    )}
                </p>
                {cat.description && <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate max-w-xs mt-0.5">{cat.description}</p>}
            </td>
            <td className="px-3 py-2.5">
                {cat.nameBn
                    ? <p className="text-[13.5px] text-zinc-700 dark:text-zinc-300">{cat.nameBn}</p>
                    : <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                {cat.descriptionBn && <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate max-w-xs mt-0.5">{cat.descriptionBn}</p>}
            </td>
            <td className="px-3 py-2.5">
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500 font-mono">{cat.slug}</span>
            </td>
            <td className="px-3 py-2.5">
                <button
                    type="button"
                    onClick={onToggleStatus}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold cursor-pointer select-none transition-colors ${STATUS_TONE[cat.status] || 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'}`}
                >
                    {cat.status === 'published' ? t('printTemplateCategoriesAdmin.statusPublished') : cat.status === 'draft' ? t('printTemplateCategoriesAdmin.statusDraft') : cat.status}
                </button>
            </td>
            <td className="px-3 py-2.5">
                <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    {fmtNum(cat._count?.templates ?? 0)}
                </span>
            </td>
            <td className="px-3 py-2.5 text-[13px] text-zinc-600 dark:text-zinc-400 tabular-nums">{fmtNum(cat.sortOrder)}</td>
            <td className="px-3 py-2.5">
                {/* Publish pill switch */}
                <label className="relative inline-flex cursor-pointer items-center">
                    <input type="checkbox" checked={isPublished} onChange={onToggleStatus} className="sr-only" />
                    <span className={`w-9 h-5 rounded-full transition-colors ${isPublished ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${isPublished ? 'translate-x-4' : ''}`} />
                </label>
            </td>
            <td className="px-3 py-2.5">
                <TooltipProvider delayDuration={150}>
                    <div className="flex items-center gap-1.5">
                        <ActionBtn label={t('printTemplateCategoriesAdmin.moveUp')} disabled={index === 0} onClick={onMoveUp} className={`${actionBtnCls} text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 disabled:opacity-40 disabled:hover:text-zinc-500`}>
                            <IconChevronUp size={14} />
                        </ActionBtn>
                        <ActionBtn label={t('printTemplateCategoriesAdmin.moveDown')} disabled={index === total - 1} onClick={onMoveDown} className={`${actionBtnCls} text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 disabled:opacity-40 disabled:hover:text-zinc-500`}>
                            <IconChevronDown size={14} />
                        </ActionBtn>
                        <ActionBtn
                            label={cat.pinned ? t('printTemplateCategoriesAdmin.unpinTooltip') : t('printTemplateCategoriesAdmin.pinTooltip')}
                            onClick={onTogglePin}
                            className={`${actionBtnCls} ${cat.pinned ? 'text-primary border-primary/40 bg-primary/5' : 'text-zinc-500 hover:text-primary'}`}
                        >
                            {cat.pinned ? <IconPinFilled size={14} /> : <IconPin size={14} />}
                        </ActionBtn>
                        <ActionBtn label={t('printTemplateCategoriesAdmin.editTooltip')} onClick={onEdit} className={`${actionBtnCls} text-zinc-600 dark:text-zinc-300 hover:text-primary`}>
                            <IconEdit size={14} />
                        </ActionBtn>
                        <ActionBtn label={t('printTemplateCategoriesAdmin.deleteTooltip')} onClick={onDelete} className={`${actionBtnCls} text-red-500 hover:text-red-600`}>
                            <IconTrash size={14} />
                        </ActionBtn>
                    </div>
                </TooltipProvider>
            </td>
        </tr>
    );
}

/* ─── Main page ──────────────────────────────────────────────────────────────── */
export default function AdminPrintTemplateCategoriesPage() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const fmtNum = (n) => isBn ? String(n ?? 0).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]) : String(n ?? 0);

    const [categories, setCategories]   = useState([]);
    const [loading, setLoading]         = useState(true);
    const [modal, setModal]             = useState({ opened: false, category: null });
    const [deleteModal, setDeleteModal] = useState({ opened: false, category: null });
    const [saving, setSaving]           = useState(false);
    const [deleting, setDeleting]       = useState(false);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [activeFormTab, setActiveFormTab] = useState('en');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const res = await printTemplateCategoryService.list();
            const sorted = (res?.data || []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
            setCategories(sorted);
        } catch {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: t('printTemplateCategoriesAdmin.notifLoadFailed'), color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const applyNewOrder = useCallback(async (newOrder) => {
        const updated = newOrder.map((cat, idx) => ({ ...cat, sortOrder: idx }));
        setCategories(updated);
        const toUpdate = updated.filter((cat) => {
            const orig = categories.find(c => c.id === cat.id);
            return orig && orig.sortOrder !== cat.sortOrder;
        });
        if (toUpdate.length === 0) return;
        try {
            await Promise.all(toUpdate.map(cat => printTemplateCategoryService.update(cat.id, { sortOrder: cat.sortOrder })));
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifReorderedTitle'), message: t('printTemplateCategoriesAdmin.notifSortSaved'), color: 'teal', autoClose: 2000 });
        } catch {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: t('printTemplateCategoriesAdmin.notifSaveOrderFailed'), color: 'red' });
            fetchCategories();
        }
    }, [categories, fetchCategories, t]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = categories.findIndex(c => c.id === active.id);
        const newIndex = categories.findIndex(c => c.id === over.id);
        applyNewOrder(arrayMove(categories, oldIndex, newIndex));
    }, [categories, applyNewOrder]);

    const handleMoveUp   = useCallback((index) => { if (index === 0) return; applyNewOrder(arrayMove(categories, index, index - 1)); }, [categories, applyNewOrder]);
    const handleMoveDown = useCallback((index) => { if (index === categories.length - 1) return; applyNewOrder(arrayMove(categories, index, index + 1)); }, [categories, applyNewOrder]);

    const openCreate = () => { setForm(EMPTY_FORM); setSlugManuallyEdited(false); setActiveFormTab('en'); setModal({ opened: true, category: null }); };
    const openEdit = (cat) => {
        setForm({ name: cat.name, nameBn: cat.nameBn || '', slug: cat.slug, description: cat.description || '', descriptionBn: cat.descriptionBn || '', status: cat.status, sortOrder: cat.sortOrder });
        setSlugManuallyEdited(true); setActiveFormTab('en'); setModal({ opened: true, category: cat });
    };

    const handleNameChange = (value) => {
        setForm(prev => ({ ...prev, name: value, slug: slugManuallyEdited ? prev.slug : slugify(value) }));
    };
    const handleSlugChange = (value) => { setSlugManuallyEdited(true); setForm(prev => ({ ...prev, slug: value })); };

    const handleSave = async () => {
        if (!form.name.trim()) { notifications.show({ title: t('printTemplateCategoriesAdmin.notifValidationTitle'), message: t('printTemplateCategoriesAdmin.notifNameRequired'), color: 'orange' }); return; }
        setSaving(true);
        try {
            const payload = { ...form, sortOrder: parseInt(form.sortOrder) || 0 };
            if (modal.category) {
                await printTemplateCategoryService.update(modal.category.id, payload);
                notifications.show({ title: t('printTemplateCategoriesAdmin.notifSavedTitle'), message: t('printTemplateCategoriesAdmin.notifCategoryUpdated'), color: 'green' });
            } else {
                await printTemplateCategoryService.create(payload);
                notifications.show({ title: t('printTemplateCategoriesAdmin.notifCreatedTitle'), message: t('printTemplateCategoriesAdmin.notifCategoryCreated'), color: 'green' });
            }
            setModal({ opened: false, category: null });
            fetchCategories();
        } catch (err) {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: err?.message || t('printTemplateCategoriesAdmin.notifSaveFailed'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.category) return;
        setDeleting(true);
        try {
            await printTemplateCategoryService.remove(deleteModal.category.id);
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifDeletedTitle'), message: t('printTemplateCategoriesAdmin.notifCategoryDeleted'), color: 'green' });
            setDeleteModal({ opened: false, category: null });
            fetchCategories();
        } catch (err) {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: err?.message || t('printTemplateCategoriesAdmin.notifDeleteFailed'), color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    // Pin/unpin a category in the user gallery — the backend enforces a single
    // pin, so locally mirror that: pinning one clears `pinned` on all others.
    const handleTogglePin = async (cat) => {
        const next = !cat.pinned;
        try {
            await printTemplateCategoryService.pin(cat.id, next);
            setCategories(prev => prev.map(c => ({ ...c, pinned: c.id === cat.id ? next : false })));
            notifications.show({
                message: next ? t('printTemplateCategoriesAdmin.notifPinned') : t('printTemplateCategoriesAdmin.notifUnpinned'),
                color: 'teal',
                autoClose: 2000,
            });
        } catch {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: t('printTemplateCategoriesAdmin.notifPinFailed'), color: 'red' });
        }
    };

    const handleToggleStatus = async (cat) => {
        const newStatus = cat.status === 'published' ? 'draft' : 'published';
        try {
            await printTemplateCategoryService.update(cat.id, { status: newStatus });
            setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, status: newStatus } : c));
        } catch {
            notifications.show({ title: t('printTemplateCategoriesAdmin.notifErrorTitle'), message: t('printTemplateCategoriesAdmin.notifStatusUpdateFailed'), color: 'red' });
        }
    };

    const inputCls = 'px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 w-full';

    return (
        <div className="w-full font-hind space-y-5 py-2 sm:py-4">

            {/* ── Header ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{t('printTemplateCategoriesAdmin.pageTitle')}</h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('printTemplateCategoriesAdmin.pageSubtitle')}</p>
                </div>
                <button
                    type="button"
                    onClick={openCreate}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
                >
                    <IconPlus size={16} stroke={2} /> {t('printTemplateCategoriesAdmin.newCategory')}
                </button>
            </div>

            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                ) : categories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <div className="w-16 h-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <IconCategory size={28} stroke={1.5} className="text-zinc-400" />
                        </div>
                        <p className="text-zinc-500 dark:text-zinc-400">{t('printTemplateCategoriesAdmin.emptyState')}</p>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={14} /> {t('printTemplateCategoriesAdmin.createFirstCategory')}
                        </button>
                    </div>
                ) : (
                    <ScrollArea>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700 text-xs font-semibold text-zinc-500 dark:text-zinc-400">
                                    <tr>
                                        <th className="w-9 px-2 py-3" />
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colName')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colBanglaName')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colSlug')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colStatus')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colTemplates')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colSort')}</th>
                                        <th className="px-3 py-3 text-left">{t('printTemplateCategoriesAdmin.colPublish')}</th>
                                        <th className="px-3 py-3 text-left" style={{ width: 160 }}>{t('printTemplateCategoriesAdmin.colActions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                        {categories.map((cat, index) => (
                                            <SortableRow
                                                key={cat.id}
                                                cat={cat}
                                                index={index}
                                                total={categories.length}
                                                fmtNum={fmtNum}
                                                onEdit={() => openEdit(cat)}
                                                onDelete={() => setDeleteModal({ opened: true, category: cat })}
                                                onToggleStatus={() => handleToggleStatus(cat)}
                                                onTogglePin={() => handleTogglePin(cat)}
                                                onMoveUp={() => handleMoveUp(index)}
                                                onMoveDown={() => handleMoveDown(index)}
                                            />
                                        ))}
                                    </SortableContext>
                                </tbody>
                            </table>
                        </DndContext>
                    </ScrollArea>
                )}
            </div>

            {/* Create / Edit Modal */}
            <Dialog open={modal.opened} onOpenChange={(v) => !v && setModal({ opened: false, category: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{modal.category ? t('printTemplateCategoriesAdmin.editCategory') : t('printTemplateCategoriesAdmin.newCategory')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-3">
                        {/* Language tabs */}
                        <div className="border-b border-zinc-200 dark:border-zinc-700">
                            <div className="flex">
                                {['en', 'bn'].map(tab => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveFormTab(tab)}
                                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeFormTab === tab ? 'border-primary text-primary' : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'}`}
                                    >
                                        {tab === 'en' ? t('printTemplateCategoriesAdmin.tabEnglish') : t('printTemplateCategoriesAdmin.tabBangla')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {activeFormTab === 'en' && (
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('printTemplateCategoriesAdmin.labelName')} <span className="text-red-500">*</span></label>
                                    <input type="text" placeholder={t('printTemplateCategoriesAdmin.placeholderName')} value={form.name} onChange={e => handleNameChange(e.target.value)} className={inputCls} />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('printTemplateCategoriesAdmin.labelDescription')}</label>
                                    <textarea rows={2} placeholder={t('printTemplateCategoriesAdmin.placeholderDescription')} value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} className={inputCls} />
                                </div>
                            </div>
                        )}

                        {activeFormTab === 'bn' && (
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">নাম (বাংলা)</label>
                                    <input type="text" placeholder="যেমন: ভিজিটিং কার্ড" value={form.nameBn} onChange={e => setForm(prev => ({ ...prev, nameBn: e.target.value }))} className={inputCls} />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">বিবরণ (বাংলা)</label>
                                    <textarea rows={2} placeholder="সংক্ষিপ্ত বিবরণ (ঐচ্ছিক)" value={form.descriptionBn} onChange={e => setForm(prev => ({ ...prev, descriptionBn: e.target.value }))} className={inputCls} />
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('printTemplateCategoriesAdmin.labelSlug')} <span className="text-red-500">*</span></label>
                            <input type="text" placeholder="business-card" value={form.slug} onChange={e => handleSlugChange(e.target.value)} className={inputCls} />
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('printTemplateCategoriesAdmin.slugHelp')}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('printTemplateCategoriesAdmin.colStatus')}</label>
                                <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))} className={inputCls}>
                                    <option value="published">{t('printTemplateCategoriesAdmin.statusPublished')}</option>
                                    <option value="draft">{t('printTemplateCategoriesAdmin.statusDraft')}</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('printTemplateCategoriesAdmin.labelSortOrder')}</label>
                                <input type="number" min={0} value={form.sortOrder} onChange={e => setForm(prev => ({ ...prev, sortOrder: e.target.value }))} className={inputCls} />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-1">
                            <button type="button" onClick={() => setModal({ opened: false, category: null })} className="px-4 py-2 text-sm rounded-lg text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                                {t('printTemplateCategoriesAdmin.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-60"
                            >
                                {saving && <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                {modal.category ? t('printTemplateCategoriesAdmin.saveChanges') : t('printTemplateCategoriesAdmin.createCategory')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={deleteModal.opened} onOpenChange={(v) => !v && setDeleteModal({ opened: false, category: null })}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('printTemplateCategoriesAdmin.deleteCategory')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {t('printTemplateCategoriesAdmin.deleteConfirmPrefix')} <strong>{deleteModal.category?.name}</strong>{t('printTemplateCategoriesAdmin.deleteConfirmSuffix')}
                    </p>
                    <div className="flex justify-end gap-2 mt-4">
                        <button type="button" onClick={() => setDeleteModal({ opened: false, category: null })} className="px-4 py-2 text-sm rounded-lg text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                            {t('printTemplateCategoriesAdmin.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={deleting}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-60"
                        >
                            {deleting && <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                            {t('printTemplateCategoriesAdmin.delete')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
